import React from 'react';
import {
  ShiftChangeRequestApprovalStatus,
  ShiftChangeRequestColleagueApprovalStatus,
  ShiftChangeRequestFragmentFragment,
} from '../../../../__generated__/graphql';
import FormattedDateTime from '../../../../../../components/datetime/FormattedDateTime';
import ConfirmModal from '../../../../../../components/Modal/ConfirmModal';
import { useUserInfo } from '../../../../context/UserInfo';
import { useNamespacedTranslation } from '../../../../../../hooks/useNamespacedTranslation';
import {
  getDetailedStatusBadgeColor,
  getColleagueBadgeColor,
} from '../shiftChangeRequestUtils';
import {
  useCancelShiftChangeRequest,
  useRespondToShiftChangeRequest,
} from '../useShiftChangeRequestActions';
import { RequestCard } from './RequestCard';
import Button from '../../../../../../components/button';

type Request = ShiftChangeRequestFragmentFragment;

type ShiftChangeRequestsListRequesterProps = {
  requests: readonly Request[];
};

export const ShiftChangeRequestsList: React.FC<
  React.PropsWithChildren<ShiftChangeRequestsListRequesterProps>
> = ({ requests }) => {
  const t = useNamespacedTranslation('schedulerShifts.shiftChangeRequest');
  const tColleagueApprovalStatus = useNamespacedTranslation(
    'schedulerShifts.receivedChangeRequests.colleagueApprovalStatus',
  );
  const { id: currentEmployeeId } = useUserInfo();
  const {
    requestToCancel,
    isCancelling,
    handleCancelClick,
    handleCancelConfirm,
    handleCancelClose,
  } = useCancelShiftChangeRequest();
  const {
    requestToRespond,
    isResponding,
    handleAcceptClick,
    handleDeclineClick,
    handleRespondConfirm,
    handleRespondClose,
  } = useRespondToShiftChangeRequest();

  const isRequesterFilter = (request: Request) => {
    return request.requestedByEmployee?.Id === currentEmployeeId;
  };
  const isApproverFilter = (request: Request) => {
    return request.proposedResourceEmployee?.Id === currentEmployeeId;
  };
  const filteredRequests = requests.filter(
    (request) => isRequesterFilter(request) || isApproverFilter(request),
  );

  return (
    <>
      <div className="d-flex flex-column gap-2">
        {filteredRequests.map((request) => {
          const isRequester = isRequesterFilter(request);
          const isApprover = isApproverFilter(request);
          const canCancel =
            request.flair__Approval_Status__c ===
              ShiftChangeRequestApprovalStatus.Pending && isRequester;
          const canRespond =
            request.flair__Colleague_Approval_Status__c ===
              ShiftChangeRequestColleagueApprovalStatus.Pending && isApprover;
          const isThisRequestResponding =
            isResponding && requestToRespond?.id === request.Id;
          const isThisRequestCancelling =
            isCancelling && requestToCancel === request.Id;

          return (
            <RequestCard
              key={request.Id}
              request={request}
              headerActions={
                <div className="d-flex align-items-center gap-2">
                  {isRequester &&
                    request.flair__Approval_Status_Detailed__c && (
                      <span
                        className={`badge ${getDetailedStatusBadgeColor(
                          request.flair__Approval_Status_Detailed__c,
                        )}`}>
                        {t(
                          `detailedStatus.${request.flair__Approval_Status_Detailed__c}`,
                        )}
                      </span>
                    )}
                  {isApprover &&
                    request.flair__Colleague_Approval_Status__c && (
                      <span
                        className={`badge ${getColleagueBadgeColor(
                          request.flair__Colleague_Approval_Status__c,
                        )}`}>
                        {tColleagueApprovalStatus(
                          `${request.flair__Colleague_Approval_Status__c}`,
                        )}
                      </span>
                    )}
                  {isApprover &&
                    request.flair__Colleague_Approval_Status__c ===
                      ShiftChangeRequestColleagueApprovalStatus.Accepted &&
                    request.flair__Approval_Status_Detailed__c && (
                      <span
                        className={`badge ${getDetailedStatusBadgeColor(
                          request.flair__Approval_Status_Detailed__c,
                        )}`}>
                        {t(
                          `detailedStatus.${request.flair__Approval_Status_Detailed__c}`,
                        )}
                      </span>
                    )}
                </div>
              }>
              {isRequester && request.proposedResourceEmployee?.Name && (
                <div className="text-muted small">
                  <strong>{t('proposedEmployee')}:</strong>{' '}
                  {request.proposedResourceEmployee.Name}
                </div>
              )}
              {isApprover && request.requestedByEmployee?.Name && (
                <div className="text-muted small">
                  <strong>{t('requestedBy')}:</strong>{' '}
                  {request.requestedByEmployee.Name}
                </div>
              )}
              {request.flair__Colleague_Responded_At__c &&
                (request.flair__Colleague_Approval_Status__c ===
                  ShiftChangeRequestColleagueApprovalStatus.Accepted ||
                  request.flair__Colleague_Approval_Status__c ===
                    ShiftChangeRequestColleagueApprovalStatus.Declined) && (
                  <div className="text-muted small">
                    <strong>
                      {request.flair__Colleague_Approval_Status__c ===
                      ShiftChangeRequestColleagueApprovalStatus.Accepted
                        ? t('colleagueResponded.acceptedAt')
                        : t('colleagueResponded.rejectedAt')}
                      :
                    </strong>{' '}
                    <FormattedDateTime
                      dateTime={request.flair__Colleague_Responded_At__c}
                      format="long"
                    />
                  </div>
                )}
              {canCancel && (
                <div className="d-flex gap-2 mt-2">
                  <Button
                    label={t('cancelRequest')}
                    isProcessing={isThisRequestCancelling}
                    disabled={isCancelling}
                    variant="outline-danger"
                    size="sm"
                    icon="close-outline"
                    onClick={() => handleCancelClick(request.Id)}
                  />
                </div>
              )}
              {canRespond && (
                <div className="d-flex gap-2 mt-2">
                  <Button
                    label={t('accept')}
                    isProcessing={
                      isThisRequestResponding &&
                      requestToRespond?.status ===
                        ShiftChangeRequestColleagueApprovalStatus.Accepted
                    }
                    disabled={isResponding}
                    variant="outline-primary"
                    size="sm"
                    icon="checkmark-outline"
                    onClick={() => handleAcceptClick(request.Id)}
                  />
                  <Button
                    label={t('decline')}
                    isProcessing={
                      isThisRequestResponding &&
                      requestToRespond?.status ===
                        ShiftChangeRequestColleagueApprovalStatus.Declined
                    }
                    disabled={isResponding}
                    icon="close-outline"
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleDeclineClick(request.Id)}
                  />
                </div>
              )}
            </RequestCard>
          );
        })}
      </div>
      <ConfirmModal
        show={!!requestToCancel}
        loading={isCancelling}
        title={t('cancelConfirmation.title')}
        text={t('cancelConfirmation.message')}
        onConfirm={handleCancelConfirm}
        onClose={handleCancelClose}
        yesLabel={t('cancelConfirmation.confirm')}
        noLabel={t('cancelConfirmation.cancel')}
      />
      <ConfirmModal
        show={!!requestToRespond}
        loading={isResponding}
        title={
          requestToRespond?.status ===
          ShiftChangeRequestColleagueApprovalStatus.Accepted
            ? t('respondConfirmation.acceptTitle')
            : t('respondConfirmation.declineTitle')
        }
        text={
          requestToRespond?.status ===
          ShiftChangeRequestColleagueApprovalStatus.Accepted
            ? t('respondConfirmation.acceptMessage')
            : t('respondConfirmation.declineMessage')
        }
        onConfirm={handleRespondConfirm}
        onClose={handleRespondClose}
        yesLabel={
          requestToRespond?.status ===
          ShiftChangeRequestColleagueApprovalStatus.Accepted
            ? t('accept')
            : t('decline')
        }
        noLabel={t('cancel')}
      />
    </>
  );
};
